import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["accessLevel", "actions", "objectId", "objectTypeId", "property", "value", "formMetadata", "enableConditionalPropertyOptions", "onFocus", "onBlur", "onChange", "formControlProps", "getAdditionalPropertyValue", "getSavedValue", "getDraftValue"];
import { HIDDEN } from 'customer-data-objects/property/AccessLevel';
import { isFile } from 'customer-data-objects/property/PropertyIdentifier';
import { useCallback, useMemo } from 'react';
import emptyFunction from 'react-utils/emptyFunction';
import { useInputAutosave } from '../../autosave/hooks/useInputAutosave';
import { useIsHidingHighlySensitiveValue } from '../../dataSensitivity/useIsHidingHighlySensitiveValue';
import { RequirablePropertyInputHighlySensitive } from '../../input/PropertyInputGetters';
import { Metrics } from '../../metrics/Metrics';
import useComponentMountMetric from '../../metrics/useComponentMountMetric';
import { usePropertyInputCanaryMetrics } from '../../metrics/usePropertyInputCanaryMetrics';
import { usePropertyValidation } from '../../validation/hooks/usePropertyValidation';
import { ActionsContext } from '../context/ActionsContext';
import { FocusProvider, useFocusContext } from '../context/FocusContext';
import { IdentifierProvider } from '../context/IdentifierContext';
import { PropertyContextProvider } from '../context/PropertyContext/PropertyContextProvider';
import useConditionalPropertiesPanel from '../hooks/useConditionalPropertiesPanel';
import { useConditionalPropertyOptions } from '../hooks/useConditionalPropertyOptions';
import useFirstCallEffect from '../hooks/useFirstCallEffect';
import { useGetAdditionalPropertyValue } from '../hooks/useGetAdditionalPropertyValue';
import { useHideFloatingEditIcon } from '../hooks/useHideFloatingEditIcon';
import { useIsPropertyReadOnly } from '../hooks/useIsPropertyReadOnly';
import { useObjectId } from '../hooks/useObjectId';
import { useObjectTypeId } from '../hooks/useObjectTypeId';
import { useProperty } from '../hooks/useProperty';
import { usePropertyAccessLevel } from '../hooks/usePropertyAccessLevel';
import { usePropertyInputComponent } from '../hooks/usePropertyInputComponent';
import { usePropertyValue } from '../hooks/usePropertyValue';
import { EDIT_INPUT_MODE } from '../types/PropertyInputV2Component';
import FormControl from './FormControl';
import InputWithFloatingFormControlFocus from './InputWithFloatingFormControlFocus';
import { useLeadStageChangeValidation } from '../../input/pipeline/hooks/leads/useLeadStageChangeValidation';
import { useAIProcessingStatus } from '../../ai/useAIProcessingStatus';
import shouldShowAIComponentForStatus from '../../ai/shouldShowAIComponentForStatus';
import { useTicketStatusChangeTracking } from '../hooks/useTicketStatusChangeTracking';
import { usePropertyInputNormalization } from '../hooks/usePropertyInputNormalization';
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
const PropertyInputV2Wrapped = props => {
  const {
    autoFocus,
    disabled,
    enableAutosave = false,
    disableValidation = false,
    enableMultipleEmailInput = false,
    enablePopovers = true,
    enableCheckboxInput = false,
    enablePhoneNumberInput = true,
    enableConditionalPropertiesPanel = false,
    conditionalPropertyRules = undefined,
    FormControl: FormControlComponent,
    formControlProps = {},
    inputMode = EDIT_INPUT_MODE,
    inputProps = {},
    inputRef,
    onChange,
    onKeyUp,
    onSave = emptyFunction,
    onTracking,
    onValidationChange = emptyFunction,
    onOpenChange = emptyFunction,
    popoverProps,
    readOnly,
    use = 'default',
    validationSettings,
    showAutoSaveText
  } = props;
  const accessLevel = usePropertyAccessLevel();
  const property = useProperty();
  const propertyValue = usePropertyValue();
  const objectId = useObjectId();
  const objectTypeId = useObjectTypeId();
  const getAdditionalPropertyValue = useGetAdditionalPropertyValue();
  const {
    onFocus,
    onBlur
  } = useFocusContext();
  const showAutoSaveTextInternal = enableAutosave || showAutoSaveText;
  const onChangeWithTicketStatusTracking = useTicketStatusChangeTracking(onChange);
  const {
    onBlur: wrappedOnBlur,
    onFocus: wrappedOnFocus,
    onValidationChange: wrappedOnValidationChange,
    onChange: wrappedOnChange,
    attemptAutosave,
    onOpenChange: wrappedOnOpenChange
  } = useInputAutosave({
    onBlur,
    onFocus,
    inputMode,
    onChange: onChangeWithTicketStatusTracking,
    onOpenChange,
    onSave,
    onValidationChange,
    enableAutosave,
    use
  });
  const {
    leadStageValidationModal,
    onChangeWithLeadStageValidation
  } = useLeadStageChangeValidation({
    onChange: wrappedOnChange,
    onApply: attemptAutosave,
    inputMode
  });
  const {
    conditionalPropertiesPanel,
    maybeOpenConditionalPropertiesPanel
  } = useConditionalPropertiesPanel({
    enableConditionalPropertiesPanel,
    conditionalPropertyRules,
    property,
    onApply: attemptAutosave,
    onChange: onChangeWithLeadStageValidation,
    objectId,
    objectTypeId,
    getAdditionalPropertyValue,
    showAutoSaveText: showAutoSaveTextInternal,
    value: propertyValue
  });
  const isPropertyReadOnly = useIsPropertyReadOnly(objectTypeId, property, accessLevel, readOnly);
  const InputComponent = usePropertyInputComponent({
    isPropertyReadOnly,
    enableMultipleEmailInput,
    enablePopovers,
    enableCheckboxInput,
    enablePhoneNumberInput,
    use
  });
  const shouldHideEditIcon = useHideFloatingEditIcon();
  const isFileProperty = isFile(property);
  const shouldRunValidation = !disabled && !isPropertyReadOnly && !disableValidation &&
  // File properties cannot have custom validation rules.
  // Also, PropertyInputFilesV2 handles it's own validation using this framework.
  // Disabling validation from usePropertyValidation avoids race conditions.
  !isFileProperty;
  const {
    onChangeSingle,
    onChangeMultiple,
    validationState
  } = usePropertyValidation({
    disableValidation: !shouldRunValidation,
    objectId,
    objectTypeId,
    onChange: maybeOpenConditionalPropertiesPanel,
    onValidationChange: wrappedOnValidationChange,
    property,
    propertyValue,
    validationSettings
  });
  const {
    invalid,
    message,
    additionalHelp
  } = validationState;
  const messageWithAdditionalHelp = additionalHelp !== undefined ? /*#__PURE__*/_jsxs(_Fragment, {
    children: [message, " ", additionalHelp]
  }) : message;
  const isHidingHighlySensitiveValue = useIsHidingHighlySensitiveValue();
  const aiProcessingStatus = useAIProcessingStatus(objectTypeId, property.name, objectId);
  if (accessLevel !== HIDDEN && isHidingHighlySensitiveValue) {
    return /*#__PURE__*/_jsx(RequirablePropertyInputHighlySensitive, {
      use: use,
      formControlProps: formControlProps,
      inputProps: inputProps
    });
  }
  if (!InputComponent) {
    return null;
  }
  const isStatic = accessLevel === HIDDEN || isFileProperty || shouldShowAIComponentForStatus(aiProcessingStatus);
  const validationError = shouldRunValidation && invalid;
  const error = !!formControlProps.error || validationError ? true : formControlProps.error;
  const validationMessage = formControlProps.validationMessage || (shouldRunValidation ? messageWithAdditionalHelp : undefined);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(FormControl, Object.assign({
      Component: FormControlComponent
    }, formControlProps, {
      error: error,
      validationMessage: validationMessage,
      readOnly: !!formControlProps.readOnly || isPropertyReadOnly || disabled,
      hideEditIcon: isPropertyReadOnly || disabled || accessLevel === HIDDEN || shouldHideEditIcon,
      isStatic: isStatic,
      formControlUse: use,
      children: /*#__PURE__*/_jsx(InputWithFloatingFormControlFocus, Object.assign({
        InputComponent: InputComponent
      }, inputProps, {
        onOpenChange: wrappedOnOpenChange,
        autoFocus: autoFocus,
        disabled: disabled,
        inputMode: inputMode,
        inputRef: inputRef,
        onBlur: wrappedOnBlur
        // These change handlers are provided by usePropertyValidation
        // so it can intercept onChange events and trigger validation
        ,
        onChange: onChangeSingle,
        onChangeMultiple: onChangeMultiple,
        onFocus: wrappedOnFocus,
        onKeyUp: onKeyUp,
        onTracking: onTracking,
        onValidationChange: wrappedOnValidationChange,
        readOnly: isPropertyReadOnly,
        formControlUse: use,
        showAutoSaveText: showAutoSaveTextInternal,
        enablePopovers: enablePopovers,
        popoverProps: popoverProps,
        error: error,
        validationMessage: validationMessage,
        disableValidation: disableValidation
        // @ts-expect-error Intentionally omitted from the types since we do not want it to be
        // used. Instead `usePropertyValue()` can be used to get the value. We need to pass this
        // down to satisfy implicit behavior of floating form controls.
        ,
        value: propertyValue
      }))
    })), conditionalPropertiesPanel, leadStageValidationModal]
  });
};
const DefaultActions = {};
const PropertyInputV2 = props => {
  const {
      accessLevel: accessLevelOverride,
      actions = DefaultActions,
      objectId,
      objectTypeId,
      property,
      value,
      formMetadata,
      enableConditionalPropertyOptions,
      onFocus = emptyFunction,
      onBlur = emptyFunction,
      onChange,
      formControlProps,
      // @ts-expect-error TS confused by union, its OK if this pulls undefined
      getAdditionalPropertyValue,
      // @ts-expect-error TS confused by union, its OK if this pulls undefined
      getSavedValue,
      // @ts-expect-error TS confused by union, its OK if this pulls undefined
      getDraftValue
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  useComponentMountMetric('PropertyInputV2');
  usePropertyInputCanaryMetrics(props);
  const {
    normalizedObjectTypeId,
    normalizedProperty,
    normalizedGetAdditionalPropertyValue
  } = usePropertyInputNormalization({
    objectTypeId,
    property,
    getAdditionalPropertyValue,
    getDraftValue,
    getSavedValue
  });
  const onChangeWithTracking = useFirstCallEffect(onChange, useCallback(() => {
    Metrics.counter('propertyinputv2-onchange', {
      type: normalizedProperty.type,
      fieldType: normalizedProperty.fieldType
    }).increment();
  }, [normalizedProperty.fieldType, normalizedProperty.type]));
  const {
    data: conditionalPropertyOptionsData
  } = useConditionalPropertyOptions({
    property: normalizedProperty,
    enableConditionalPropertyOptions,
    objectTypeId: normalizedObjectTypeId,
    objectId,
    getAdditionalPropertyValue: normalizedGetAdditionalPropertyValue,
    value
  });
  const formControlPropsWithConditionalPropertyOptions = useMemo(() => {
    if (!conditionalPropertyOptionsData) {
      return formControlProps;
    }

    // We want to show any validation errors from the conditional property options.
    // But, if the host app passed us an error, we defer to that instead.
    const {
      error,
      validationMessage
    } = conditionalPropertyOptionsData;
    if (formControlProps && formControlProps.error || !error) {
      return formControlProps;
    }
    return Object.assign({}, formControlProps, {
      error,
      validationMessage
    });
  }, [conditionalPropertyOptionsData, formControlProps]);
  const actionsContextValue = useMemo(() => ({
    actions
  }), [actions]);
  return /*#__PURE__*/_jsx(PropertyContextProvider, {
    accessLevelOverride: accessLevelOverride,
    objectTypeId: normalizedObjectTypeId,
    objectId: objectId,
    value: value,
    property: conditionalPropertyOptionsData ? conditionalPropertyOptionsData.property : normalizedProperty,
    autoManageHighlySensitiveValue: false,
    getAdditionalPropertyValue: normalizedGetAdditionalPropertyValue,
    children: /*#__PURE__*/_jsx(IdentifierProvider, {
      formMetadata: formMetadata,
      children: /*#__PURE__*/_jsx(ActionsContext.Provider, {
        value: actionsContextValue,
        children: /*#__PURE__*/_jsx(FocusProvider, {
          onFocus: onFocus,
          onBlur: onBlur,
          children: /*#__PURE__*/_jsx(PropertyInputV2Wrapped, Object.assign({}, rest, {
            onChange: onChangeWithTracking,
            formControlProps: formControlPropsWithConditionalPropertyOptions
          }))
        })
      })
    })
  });
};
export default PropertyInputV2;